import { zodResolver } from "@hookform/resolvers/zod";
import { Stepper } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/ui/pageHeader";
import { ArrowLeft, Edit, Eye } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as z from "zod";
import { useSopDetailsForEdit } from "../../hooks";
import {
  DEFAULT_SOP_FORM_DATA,
  InitialSetupData,
  SopFormData,
} from "../../types";
import CreateSopForm from "./stepComponents/CreateSopForm";
import SopBasicInfoStep from "./stepComponents/SopBasicInfoStep";
import SopHelpStep from "./stepComponents/SopHelpStep";
import SopMaterialsStep from "./stepComponents/SopMaterialsStep";
import SopReadingsStep from "./stepComponents/SopReadingsStep";
import SopReviewStep from "./stepComponents/SopReviewStep";
import SopSafetyStep from "./stepComponents/SopSafetyStep";
import SopTasksStep from "./stepComponents/SopTasksStep";

// Initial form schema for step 0
const initialSopSchema = z
  .object({
    name: z.string().min(3, "SOP name must be at least 3 characters"),
    description: z.string().min(1, "Description is required"),
    utilityType: z.string().min(1, "Please select a utility type"),
    associatedWith: z.string().min(1, "Please select an association"),
    assetType: z.string().optional(),
    actionType: z.string().optional(),
    operationType: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.associatedWith === "asset") {
        return (
          data.assetType &&
          data.assetType.length > 0 &&
          data.actionType &&
          data.actionType.length > 0 &&
          data.operationType &&
          data.operationType.length > 0
        );
      }
      return true;
    },
    {
      message: "Asset fields are required when association is set to 'asset'",
      path: ["associatedWith"],
    }
  );

// Sample SOP data
const getSopById = (id: string): SopFormData | null => {
  const templates: Record<string, SopFormData> = {
    "SOP-001": {
      ...DEFAULT_SOP_FORM_DATA,
      initialSetup: {
        name: "Water Meter Installation",
        description: "Standard procedure for installing new water meters",
        utilityType: "water",
        associatedWith: "meter",
        assetType: "",
        actionType: "",
        operationType: "",
      },
      basicInfo: {
        ...DEFAULT_SOP_FORM_DATA.basicInfo,
        sopName: "Water Meter Installation",
        description: "Standard procedure for installing new water meters",
        utilityType: "water",
        priority: "high",
        associatedWith: "meter",
        shortName: "WMI-001",
        type: "Installation",
        subType: "Residential",
        sla: "24 hours",
        estimatedLabourCost: "350",
        taxRate: "10",
        requiredFieldforceCount: "2",
        assetType: "Meter",
        actionType: "Installation",
        operationType: "Installation",
      },
      safety: {
        ...DEFAULT_SOP_FORM_DATA.safety,
        ppe: ["hard-hat", "safety-glasses", "gloves"],
        riskLevel: "medium",
        safetyNotes: "Follow all safety protocols and wear PPE at all times.",
      },
      tasks: [
        {
          id: 1,
          title: "Initial Inspection",
          description: "Inspect the location and equipment",
          duration: 15,
          mandatory: true,
          downtime: 0,
          skills: ["inspection"],
          photoRequired: true,
        },
        {
          id: 2,
          title: "Setup Equipment",
          description: "Prepare and setup necessary tools",
          duration: 20,
          mandatory: true,
          downtime: 0,
          skills: ["technical"],
          photoRequired: false,
        },
      ],
      materials: [
        { name: "Water Meter", quantity: "1" },
        { name: "Pipe Fittings", quantity: "4" },
      ],
      equipment: [
        { name: "Pressure Gauge", condition: "good" },
        { name: "Flow Meter", condition: "good" },
      ],
      readings: [
        {
          name: "Initial Pressure",
          unit: "PSI",
          min: "30",
          max: "60",
        },
      ],
      resources: [
        {
          id: 1,
          name: "Installation Manual",
          type: "file",
          content: "https://example.com/manuals/meter_installation.pdf",
        },
      ],
      contacts: [
        {
          id: 1,
          name: "Technical Support",
          role: "Support Team",
          phone: "555-123-4567",
          email: "support@utility.com",
        },
      ],
      fieldNotes: "Document any unusual conditions at the installation site.",
      suggestionPrompt:
        "How could we improve this SOP to make installation easier?",
      allowRating: true,
      trackCompletion: true,
    },
  };

  return templates[id] || null;
};

//this is for editing
const transformApiDataToFormData = (apiData: any): SopFormData => {
  return {
    initialSetup: {
      name: apiData.name || "",
      description: apiData.description || "",
      utilityType: apiData.utilityServices?.[0] || "",
      associatedWith: apiData.sopAssociationType?.toString() || "",
      assetType: apiData.additionalData?.assetTypes || [],
      systemType: apiData.additionalData?.systemTypes || [],
      facilityType: apiData.additionalData?.facilityTypes || [],
      networkType: apiData.additionalData?.networkTypes || [],
      actionType: "",
      operationType: apiData.operationType
        ? apiData.operationType.toString()
        : "",
    },
    basicInfo: {
      sopName: apiData.name || "",
      shortName: apiData.shortName || "",
      description: apiData.description || "",
      utilityType: apiData.utilityServices?.[0] || "",
      type: apiData.sopType || "",
      typeCode: apiData.sopType || "",
      subType: apiData.sopSubtype || "",
      subTypeCode: apiData.sopSubtype || "",
      priority: apiData.priority?.toString() || "medium",
      associatedWith: apiData.sopAssociationType?.toString() || "",
      sla: apiData.rulesDuration ? apiData.rulesDuration.toString() : "",
      estimatedLabourCost: apiData.rate ? apiData.rate.toString() : "",
      estimatedOtherCost: apiData.estimatedOtherCost ? apiData.estimatedOtherCost.toString() : "",   
      taxRate: apiData.tax,
      requiredFieldforceCount: apiData.staffRequired
        ? apiData.staffRequired.toString()
        : "",
      assetType: apiData.additionalData?.assetTypes || [],
      systemType: apiData.additionalData?.systemTypes || [],
      facilityType: apiData.additionalData?.facilityTypes || [],
      networkType: apiData.additionalData?.networkTypes || [],
      actionType: "",
      operationType: apiData.operationType
        ? apiData.operationType.toString()
        : "",
      slaHours: apiData.slaHours || "",
    },
    safety: {
      safetyProtocols:
        apiData.safetyRequirements?.safetyProtocol?.map((p: any) => p.id) || [],
      ppe:
        apiData.safetyRequirements?.safetyEquipment?.map((e: any) => e.id) ||
        [],
      selectedPackageId: apiData.safetyRequirements?.package?.id || null,
      safetyPackage: apiData.safetyRequirements?.package?.id?.toString() || "",
      riskLevel: apiData.safety?.riskLevel || "",
      safetyNotes: apiData.safety?.safetyNotes || "",
      fieldRealities: apiData.safety?.fieldRealities || "",
      environmentalVariables: apiData.safety?.environmentalVariables || [],
    },
    tasks:
      apiData.task?.map((task: any, index: number) => ({
        id: index + 1,
        title: task.name || "", // Map 'name' to 'title'
        description: task.description || "", // Add description field
        duration: parseInt(task.time) || 0, // Map 'time' to 'duration' and convert to number
        downtime: parseInt(task.downtime) || 0, // Convert downtime to number
        skills: task.skillId?.map((id: number) => id.toString()) || [], // Convert skill IDs to strings
        photoRequired: task.isPhotoReq === true || task.isPhotoReq === "Yes", // Handle boolean/string conversion
      })) || [],
    materials:
      apiData.material?.map((material: any) => ({
        // 🔧 FIXED: Use 'material'
        name: material.name || "",
        quantity: material.quantityRequired || "", // 🔧 FIXED: Use quantityRequired
        size: material.size || "",
        cost: material.cost || "",
      })) || [],
    equipment: apiData.equipment || [],
    readings: apiData.readings || [],
    helpContent: apiData.helpContent || "",
    resources: apiData.resources || [],
    contacts: apiData.contacts || [],
    fieldNotes: apiData.fieldNotes || "",
    suggestionPrompt: apiData.suggestionPrompt || "",
    allowRating: apiData.allowRating || false,
    trackCompletion: apiData.trackCompletion || false,
    helpDocuments: apiData.documents || [],
  };
};

const CreateSopPage = () => {
  const [formData, setFormData] = useState<SopFormData>({
    ...DEFAULT_SOP_FORM_DATA,
  });

  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || "create";
  const templateId = queryParams.get("id") || "";

  // Determine if we're in view-only mode
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  // ✅ FIXED: Enhanced Initial Setup Form with validation fixes
  const initialSetupForm = useForm<z.infer<typeof initialSopSchema>>({
    resolver: zodResolver(initialSopSchema),
    mode: "onChange", // Enable validation on change - CRITICAL FIX!
    defaultValues: {
      name: "",
      description: "",
      utilityType: "",
      associatedWith: "",
      assetType: "",
      actionType: "",
      operationType: "",
    },
  });

  // ✅ FIXED: Move hook call to top level with conditional enabled
  const {
    data: sopData,
    isLoading: isSopLoading,
    error: sopError,
    refetch,
  } = useSopDetailsForEdit(isEditMode && templateId ? templateId : "");

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  // Cleanup local storage on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem("sop-builder-progress");
      if (templateId) {
        localStorage.removeItem(`sop-builder-progress-${templateId}`);
      }
    };
  }, [templateId]);

  // ✅ ENHANCED: Simplified useEffect with step helper integration
  React.useEffect(() => {
    if (!templateId) return;

    if (isEditMode && sopData && !isSopLoading && sopData.result) {
      const transformedData = transformApiDataToFormData(sopData.result);

      // ✅ FIXED: Always use API data for edit mode, don't override with localStorage
      setFormData(transformedData);

      // ✅ NEW: Update localStorage with current data for edit mode
      const sopSpecificKey = `sop-builder-progress-${templateId}`;
      const stepperData = {
        currentStep: 0,
        completedSteps: [0, 1, 2, 3, 4, 5, 6, 7], // Mark all as completed for edit
        skippedSteps: [],
        stepData: transformedData,
        timestamp: Date.now(),
        version: "1.0.0",
        sopId: templateId,
      };

      localStorage.setItem(sopSpecificKey, JSON.stringify(stepperData));
      localStorage.setItem("sop-builder-progress", JSON.stringify(stepperData));

      // Update initial setup form with better data handling and type safety
      const formResetData = {
        name: transformedData.initialSetup?.name || "",
        description: transformedData.initialSetup?.description || "",
        utilityType: transformedData.initialSetup?.utilityType || "",
        associatedWith: transformedData.initialSetup?.associatedWith || "",
        assetType: transformedData.initialSetup?.assetType || "",
        actionType: transformedData.initialSetup?.actionType || "",
        operationType: transformedData.initialSetup?.operationType || "",
      };

      // Reset form and trigger validation
      initialSetupForm.reset(formResetData);

      // ✅ CRITICAL FIX: Trigger validation after reset to ensure form state is correct
      setTimeout(() => {
        initialSetupForm.trigger();
      }, 100);

      toast({
        title: "Editing SOP",
        description: `Editing SOP: ${transformedData.initialSetup.name}`,
        duration: 3000,
      });
    } else if (!isEditMode) {
      // Handle create mode (either from template or brand new)
      const sopDataSample = templateId ? getSopById(templateId) : DEFAULT_SOP_FORM_DATA;
      
      if (sopDataSample) {
        // ✅ NEW: Check for existing localStorage data in create mode
        let finalData = sopDataSample;

        try {
          const savedData = localStorage.getItem("sop-builder-progress");
          if (savedData) {
            const parsed = JSON.parse(savedData);
            const isRecent =
              parsed.timestamp && Date.now() - parsed.timestamp < 3600000; // 1 hour

            // Only resume if the saved data is for the SAME templateId (or both are "new")
            const savedSopId = parsed.sopId || "new";
            const currentSopId = templateId || "new";
            
            if (isRecent && Object.keys(parsed.stepData || {}).length > 0 && savedSopId === currentSopId) {
              finalData = parsed.stepData;
            } else {
              // Different SOP, clear old progress
              localStorage.removeItem("sop-builder-progress");
            }
          }
        } catch (error) {}

        setFormData(finalData);

        // ✅ NEW: Save to localStorage for create mode
        const stepperData = {
          currentStep: 0,
          completedSteps: [],
          skippedSteps: [],
          stepData: finalData,
          timestamp: Date.now(),
          version: "1.0.0",
          sopId: templateId || "new",
        };
        localStorage.setItem(
          "sop-builder-progress",
          JSON.stringify(stepperData)
        );

        const formResetData = {
          name: finalData.basicInfo?.sopName || "",
          description: finalData.basicInfo?.description || "",
          utilityType: finalData.basicInfo?.utilityType || "",
          associatedWith: finalData.basicInfo?.associatedWith || "",
          assetType: finalData.basicInfo?.assetType || "",
          actionType: finalData.basicInfo?.actionType || "",
          operationType: finalData.basicInfo?.operationType || "",
        };

        initialSetupForm.reset(formResetData);

        // Trigger validation for view/create mode too
        setTimeout(() => {
          initialSetupForm.trigger();
        }, 100);

        if (isViewMode && templateId) {
          toast({
            title: "SOP Loaded in View Mode",
            description: `Viewing SOP: ${sopDataSample.basicInfo?.sopName}`,
            duration: 3000,
          });
        }
      } else if (templateId) {
        toast({
          title: "Template Not Found",
          description: `No template found with ID: ${templateId}`,
          variant: "destructive",
          duration: 3000,
        });
        navigate("/service-orders/wx-master");
      }
    }
  }, [
    templateId,
    isEditMode,
    isViewMode,
    sopData,
    isSopLoading,
    toast,
    navigate,
    initialSetupForm,
  ]);

  // Handle API errors
  React.useEffect(() => {
    if (sopError && isEditMode) {
      toast({
        title: "Error Loading SOP",
        description: "An error occurred while loading SOP data.",
        variant: "destructive",
        duration: 3000,
      });
      navigate("/service-orders/wx-master");
    }
  }, [sopError, isEditMode, toast, navigate]);

  const handleFormDataChange = useCallback((updates: Partial<SopFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  // ✅ FIXED: Enhanced submit handler with better validation
  const handleInitialSetupSubmit = useCallback(
    (data: z.infer<typeof initialSopSchema>) => {
      // Validate data before proceeding
      if (
        !data.name ||
        !data.description ||
        !data.utilityType ||
        !data.associatedWith
      ) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required fields",
          variant: "destructive",
          duration: 3000,
        });
        return false;
      }

      // Create InitialSetupData that matches your interface
      const initialSetupData: InitialSetupData = {
        name: data.name,
        description: data.description,
        utilityType: data.utilityType,
        associatedWith: data.associatedWith,
        assetType: data.assetType,
        actionType: data.actionType,
        operationType: data.operationType,
      };

      // Update formData with initial setup - now type-safe with your types
      setFormData((prev) => ({
        ...prev,
        initialSetup: initialSetupData,
        basicInfo: {
          ...prev.basicInfo,
          sopName: data.name,
          description: data.description,
          utilityType: data.utilityType as any,
          associatedWith: data.associatedWith as any,
          assetType: data.assetType,
          actionType: data.actionType,
          operationType: data.operationType,
        },
      }));

      toast({
        title: "Initial Setup Completed",
        description: "Moving to Basic Information",
      });

      return true;
    },
    [toast]
  );

  // ✅ ENHANCED: handleStepSave with localStorage management
  const handleStepSave = useCallback(
    async (stepIndex: number, stepData: any, isDirty: boolean) => {
      try {
        // Update the formData based on which step is being saved
        let updatedFormData = { ...formData };

        switch (stepIndex) {
          case 0: // Basic Information (formerly Initial Setup)
            updatedFormData = {
              ...updatedFormData,
              basicInfo: stepData,
            };
            break;
          case 1: // Safety
            updatedFormData = {
              ...updatedFormData,
              safety: stepData,
            };
            break;
          case 2: // Tasks
            updatedFormData = {
              ...updatedFormData,
              tasks: stepData,
            };
            break;
          case 3: // Materials
            updatedFormData = {
              ...updatedFormData,
              materials: stepData.materials || [],
              equipment: stepData.equipment || [],
            };
            break;
          case 4: // Readings
            updatedFormData = {
              ...updatedFormData,
              readings: stepData,
            };
            break;
          case 5: // Help & Resources
            updatedFormData = {
              ...updatedFormData,
              helpContent: stepData.helpContent || "",
              resources: stepData.resources || [],
              contacts: stepData.contacts || [],
            };
            break;
          case 6: // Review & Publish
            // Final step - no additional data to save
            break;
          default:
        }

        // Update React state
        setFormData(updatedFormData);

        // ✅ NEW: Update localStorage with the new data
        const sopSpecificKey = templateId
          ? `sop-builder-progress-${templateId}`
          : null;
        const storageKey = "sop-builder-progress";

        // ✅ FIXED: Get current stepper state from localStorage FIRST
        let currentStepperState = {
          currentStep: stepIndex,
          completedSteps: [],
          skippedSteps: [],
          stepData: updatedFormData,
          timestamp: Date.now(),
          version: "1.0.0",
          sopId: templateId || "new",
        };

        try {
          const existingData = localStorage.getItem(
            sopSpecificKey || storageKey
          );
          if (existingData) {
            const parsed = JSON.parse(existingData);
            currentStepperState = {
              ...currentStepperState,
              currentStep: parsed.currentStep || stepIndex,
              completedSteps: parsed.completedSteps || [],
              skippedSteps: parsed.skippedSteps || [],
            };
          }
        } catch (error) {
          console.warn("Error parsing localStorage data:", error);
        }

        // ✅ FIXED: Mark the current step as completed AFTER getting existing data
        if (!currentStepperState.completedSteps.includes(stepIndex)) {
          currentStepperState.completedSteps = [
            ...currentStepperState.completedSteps,
            stepIndex,
          ];
        }

        // Update stepData with new information
        currentStepperState.stepData = updatedFormData;
        currentStepperState.timestamp = Date.now();

        // Save to localStorage
        localStorage.setItem(storageKey, JSON.stringify(currentStepperState));
        if (sopSpecificKey) {
          localStorage.setItem(
            sopSpecificKey,
            JSON.stringify(currentStepperState)
          );
        }

        console.log(
          "✅ Step",
          stepIndex,
          "marked as completed. Completed steps:",
          currentStepperState.completedSteps
        );

        // For edit mode, you could call an API to save the step data
        if (isEditMode && templateId && isDirty) {
          try {
            // TODO: Replace with actual API call
            // await updateSopStep(templateId, stepIndex, stepData);
          } catch (apiError) {
            // Don't return error here, localStorage save should still work
          }
        }
      } catch (error) {
        console.error("Error in handleStepSave:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    },
    [isEditMode, templateId, formData]
  );

  const handleSaveAsDraft = useCallback(() => {
    toast({
      title: "SOP Saved as Draft",
      description: "Your progress has been saved.",
    });
    navigate("/service-orders/wx-master");
  }, [toast, navigate]);

  const handleComplete = useCallback(
    async (allStepData: any, completedSteps: any, skippedSteps: any) => {
      // ✅ NEW: Clear localStorage on completion (like PlanStepper does)
      localStorage.removeItem("sop-builder-progress");
      if (templateId) {
        localStorage.removeItem(`sop-builder-progress-${templateId}`);
      }

      toast({
        title: isEditMode
          ? "SOP Updated Successfully"
          : "SOP Created Successfully",
        description: isEditMode
          ? "Your SOP has been updated."
          : "Your SOP has been created and saved as draft.",
      });
      navigate("/service-orders/wx-master");
    },
    [formData, isEditMode, toast, navigate, templateId]
  );

  const handleStepChange = useCallback(
    (stepIndex: number, previousStepIndex: number, stepInfo: any) => {},
    []
  );

  // ✅ FIXED: Enhanced step components with debugging
  const renderInitialSetupStep = useCallback(() => {
    return (
      <CreateSopForm
        form={initialSetupForm}
        onSubmit={handleInitialSetupSubmit}
        readOnly={isViewMode}
      />
    );
  }, [initialSetupForm, handleInitialSetupSubmit, isViewMode]);

  const renderBasicInfoStep = useCallback(() => {
    return (
      <SopBasicInfoStep
        state={formData}
        onUpdateState={handleFormDataChange}
        readOnly={isViewMode}
        onStepSave={handleStepSave}
      />
    );
  }, [formData, handleFormDataChange, isViewMode, handleStepSave]);

  const renderSafetyStep = useCallback(() => {
    return (
      <SopSafetyStep
        state={formData}
        onUpdateState={handleFormDataChange}
        readOnly={isViewMode}
        templateId={templateId}
        onStepSave={handleStepSave}
      />
    );
  }, [formData, handleFormDataChange, isViewMode]);

  const renderTasksStep = useCallback(() => {
    return (
      <SopTasksStep
        state={formData}
        onUpdateState={handleFormDataChange}
        readOnly={isViewMode}
        templateId={templateId}
        onStepSave={handleStepSave}
      />
    );
  }, [formData, handleFormDataChange, isViewMode]);

  const renderMaterialsStep = useCallback(
    () => (
      <SopMaterialsStep
        state={formData}
        onUpdateState={handleFormDataChange}
        readOnly={isViewMode}
        templateId={templateId}
        onStepSave={handleStepSave}
      />
    ),
    [formData, handleFormDataChange, isViewMode]
  );

  const renderReadingsStep = useCallback(
    () => (
      <SopReadingsStep
        state={formData}
        onUpdateState={handleFormDataChange}
        readOnly={isViewMode}
        templateId={templateId}
        onStepSave={handleStepSave}
      />
    ),
    [formData, handleFormDataChange, isViewMode]
  );

  const renderHelpStep = useCallback(
    () => (
      <SopHelpStep
        state={formData}
        onUpdateState={handleFormDataChange}
        readOnly={isViewMode}
        templateId={templateId}
        onStepSave={handleStepSave}
      />
    ),
    [formData, handleFormDataChange, isViewMode]
  );

  const renderReviewStep = useCallback(
    () => (
      <SopReviewStep
        state={formData}
        onUpdateState={handleFormDataChange}
        onSubmit={() => navigate("/service-orders/wx-master")}
        onSaveAsDraft={handleSaveAsDraft}
        readOnly={isViewMode}
        submitLabel={
          isViewMode
            ? "Back to Master"
            : isEditMode
            ? "Save Changes"
            : "Create SOP"
        }
        templateId={templateId}
        onStepSave={handleStepSave}
      />
    ),
    [
      formData,
      handleFormDataChange,
      navigate,
      handleSaveAsDraft,
      isViewMode,
      isEditMode,
    ]
  );

  // ✅ FIXED: Memoize the steps array properly
  const steps = useMemo(
    () => [
      {
        title: "Basic Information",

        slug: "basic-info",
        component: renderBasicInfoStep(),
        optional: false,
        infoTitle: "Basic Information Help",
        infoDescription:
          "Enter comprehensive details about the SOP including priority, costs, and resource requirements.",
      },
      {
        title: "Safety Requirements",

        slug: "safety-requirements",
        component: renderSafetyStep(),
        optional: false,
        infoTitle: "Safety Requirements Help",
        infoDescription:
          "Configure safety protocols, PPE requirements, and environmental considerations for this procedure.",
      },
      {
        title: "Task Definition",

        slug: "task-definition",
        component: renderTasksStep(),
        optional: false,
        infoTitle: "Task Definition Help",
        infoDescription:
          "Define individual tasks, their duration, required skills, and completion requirements.",
      },
      {
        title: "Materials & Equipment",

        slug: "materials-equipment",
        component: renderMaterialsStep(),
        optional: false,
        infoTitle: "Materials & Equipment Help",
        infoDescription:
          "Specify all materials, tools, and equipment needed to complete this procedure.",
      },
      {
        title: "Readings & Data",

        slug: "readings-data",
        component: renderReadingsStep(),
        optional: false,
        infoTitle: "Readings & Data Help",
        infoDescription:
          "Configure data collection requirements including measurements, readings, and documentation needs.",
      },
      {
        title: "Help & Resources",

        slug: "help-resources",
        component: renderHelpStep(),
        optional: false,
        infoTitle: "Help & Resources",
        infoDescription:
          "Add support materials, documentation, and contact information to assist technicians.",
      },
      {
        title: "Review & Publish",

        slug: "review-publish",
        component: renderReviewStep(),
        optional: false,
        infoTitle: "Review & Publish Help",
        infoDescription:
          "Review all configuration details and publish your SOP for use by field technicians.",
      },
    ],
    [
      renderInitialSetupStep,
      renderBasicInfoStep,
      renderSafetyStep,
      renderTasksStep,
      renderMaterialsStep,
      renderReadingsStep,
      renderHelpStep,
      renderReviewStep,
    ]
  );

  // Show loading state for edit mode
  if (isEditMode && isSopLoading) {
    return <div className="space-y-6 animate-fade-in"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="SOP Builder"
        description={
          isViewMode
            ? "View details of this Standard Operating Procedure"
            : isEditMode
            ? "Edit this Standard Operating Procedure"
            : "Create and configure a new Standard Operating Procedure"
        }
        onBack={() => navigate("/service-orders/wx-master")}
      >
        {isViewMode && (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 flex items-center gap-1"
          >
            <Eye className="h-3 w-3" /> Viewing SOP: #{templateId}
          </Badge>
        )}
        {isEditMode && (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 flex items-center gap-1"
          >
            <Edit className="h-3 w-3" /> Editing SOP: #{templateId}
          </Badge>
        )}
      </PageHeader>

      {/* Shared Stepper Component */}
      <Stepper
        steps={steps}
        onStepChange={handleStepChange}
        onComplete={handleComplete}
        onStepSave={handleStepSave}
        enableApiSave={true}
        saveOnStepChange={true}
        trackDirtyState={true}
        autoSave={true}
        persistProgress={true}
        allowSkip={false}
        storageKey={
          templateId && isEditMode
            ? `sop-builder-progress-${templateId}`
            : "sop-builder-progress"
        }
        enableRouting={true}
        onValidation={undefined}
        showDraftResume={true}
      />
    </div>
  );
};

export default CreateSopPage;
